import context from '@truckdown/components-systems';
import { toRaw } from 'vue';
import { Constants, ServiceModel, ServiceType } from '@truckdown/systems';
import { EntryModel, HoursEntryModel, ILocationEditManager, Listing, LocationModel } from '@truckdown/locations';

export type DayKey = keyof typeof DAYS;
export const messageEventName = 'show-message';
const TOWING = 'TOW';
const MOBILE = ['MRS', 'MTIRE'];
const CARRYOVER_CUTOFF = "06:00";
export const MINUTES_IN_DAY = 1440;
export const MIDNIGHT = "00:00";

export const DAYS = {
    Monday: 0,
    Tuesday: 1,
    Wednesday: 2,
    Thursday: 3,
    Friday: 4,
    Saturday: 5,
    Sunday: 6,
};
export interface IServiceListModel {
    code: string,
    name: string,
    detail?: string,
    addFee: boolean,
    availableAmenities: IServiceListModel[],
    selectedAmenities: IServiceListModel[],
    sites: any,
    priority: number,
    hasData: boolean
};

export function showMessage(message: string, messageType: string, dismissable: boolean, autoDismiss: boolean) {
    context.events.emit(messageEventName, {
        message: message,
        messageType: messageType,
        dismissable: dismissable,
        autoDismiss: autoDismiss
    });
}

export function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

export function haveSameValues<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, i) => val === sorted2[i]);
}

export function deepCloneWithDates<T>(obj: T): T {
    return JSON.parse(JSON.stringify(toRaw(obj)), (key, value) => {
        // ISO strings back to Date
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
            const date = new Date(value);
            return isNaN(date.getTime()) ? value : date;
        }
        return value;
    });
}

export function formatDate(dateObj: Date | string): string {
    let options: Intl.DateTimeFormatOptions = { timeZone: "UTC", year: "numeric", month: "numeric", day: "numeric" };
    if (dateObj)
        return new Date(dateObj).toLocaleDateString(undefined, options);
    else
        return "N/A";
};

export function formatDateRange(range: [Date, Date]): string {
    const format = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}/${m}/${d}`;
    };

    const [start, end] = range;
    if (!end || start.getTime() === end.getTime()) {
        return format(start);
    }

    return `${format(start)} - ${format(end)}`;
}


export function getAfterHoursType(listing?: Listing) {
    if (listing) {
        if (listing.services.some(s => MOBILE.some(m => s.code == m))) {
            return "Road Service";
        }

        if (listing.services.some(s => s.code == TOWING)) {
            return "Towing Service";
        }

        return "Emergency Service";
    } else {
        return "After Hours Service"
    }
    
};

export function isOps() {
    return context.isInRole(Constants.Roles.SystemRole);
};

export function isCsrOrAdmin() {
    return context.isInRole(Constants.Roles.SystemRole) || context.isInRole(Constants.Roles.CSRRole);
};

export function isCustomerAdmin() {
    return context.isInRole(Constants.Roles.CustomerAdminRole);
};

export function checkAdminMode() {
    const params = new URLSearchParams(window.location.search);
    const isAdminRole = context.isInRole(Constants.Roles.SystemRole) || context.isInRole(Constants.Roles.CSRRole)
    return params.has('admin') && params.get('admin') === 'true' && isAdminRole;
};

export function timeToMinutes(time: string | null): number {
    if (time) {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
    } else {
        return 0;
    }
}

export function minutesToTime(mins: number): string {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export function requiresMidnightSplit(start: string, end: string): boolean {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);

    // Ignore placeholder entries
    if (startMinutes === 0 && endMinutes === 0) return false;

    return endMinutes > 0 && endMinutes < startMinutes;
}

export function getTimeRange(entry: EntryModel): [number, number] {
    const start = timeToMinutes(entry.startTime);
    const end = timeToMinutes(entry.endTime);
    return end <= start ? [start, end + 1440] : [start, end]; // Handle overnight ranges
}

export function rangesOverlap(a: [number, number], b: [number, number]): boolean {
    return a[0] < b[1] && b[0] < a[1];
}

export function splitEntryAcrossMidnight(entry: EntryModel, currentDay: DayKey): { [key: string]: EntryModel[] } {
    const result: { [key: string]: EntryModel[] } = {};
    const nextDay = Object.keys(DAYS)[(DAYS[currentDay] + 1) % 7] as DayKey;

    result[currentDay] = [
        {
            ...entry,
            endTime: MIDNIGHT,
        },
    ];

    result[nextDay] = [
        {
            ...entry,
            startTime: MIDNIGHT,
        },
    ];
    return result;
}

export function prepareApiEntriesFromUi(input: { [key: string]: HoursEntryModel }): { [key: string]: HoursEntryModel } {
    const result: { [key: string]: HoursEntryModel } = {};

    for (const [day, group] of Object.entries(input)) {
        const entries = group.entries || [];

        for (const entry of entries) {
            if (requiresMidnightSplit(entry.startTime, entry.endTime)) {
                const split = splitEntryAcrossMidnight(entry, day as DayKey);

                for (const [splitDay, splitEntries] of Object.entries(split)) {
                    if (!result[splitDay]) {
                        result[splitDay] = {
                            enabled: false,
                            afterHours24: false,
                            entries: [],
                        };
                    }
                    result[splitDay].entries.push(...splitEntries);
                }
            } else {
                if (!result[day]) {
                    result[day] = {
                        enabled: true,
                        afterHours24: false,
                        entries: [],
                    };
                }
                result[day].entries.push({ ...entry });
            }
        }

        // Preserve day metadata — but don't overwrite existing entries!
        if (!result[day]) {
            result[day] = {
                enabled: group.enabled,
                afterHours24: group.afterHours24,
                entries: [],
            };
        } else {
            result[day].enabled = group.enabled;
            result[day].afterHours24 = group.afterHours24;
        }
    }
    return result;
}

export function getOrderedDayKeys(): DayKey[] {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
}

export function mergeMidnightEntriesFromApi(
    input: { [key: string]: HoursEntryModel }
): { [key: string]: HoursEntryModel } {

    const result: { [key: string]: HoursEntryModel } = {};
    const dayOrder = getOrderedDayKeys();
    const cloned = deepClone(input);

    // 00:00–00:00 is a "24 hours" record and must NEVER be used as a continuation piece
    const isTwentyFourHourGuard = (e: EntryModel): boolean =>
        e.startTime === "00:00" && e.endTime === "00:00";

    // A "real" continuation piece is next-day 00:00 start of same type with a real end time (not 00:00)
    const isRealContinuationPiece = (e: EntryModel, type: number): boolean =>
        e.startTime === "00:00" && e.type === type && e.endTime !== "00:00";

    // True only when an entry actually continues into the next day
    // (i.e., next day has a matching 00:00 start of the same type AND it's not the 24h record).
    const hasNextDayContinuation = (
        next: HoursEntryModel | undefined,
        entry: EntryModel
    ): boolean => {
        return !!next?.entries?.some((e) => isRealContinuationPiece(e, entry.type));
    };

    // Treat endTime "00:00" as end-of-day (24:00) unless there is explicit real continuation.
    const isCrossingMidnight = (
        entry: EntryModel,
        next: HoursEntryModel | undefined
    ): boolean => {
        const startMin = timeToMinutes(entry.startTime);
        let endMin = timeToMinutes(entry.endTime);

        // 00:00-00:00 is "24 hours" record (not a split)
        if (isTwentyFourHourGuard(entry)) return false;

        if (entry.endTime === "00:00" && entry.startTime !== "00:00") {
            // Only consider this as "crossing" if next day has a REAL matching 00:00 start
            if (hasNextDayContinuation(next, entry)) return true;

            endMin = 24 * 60; // 1440 = treat as end of day
        }

        return endMin < startMin;
    };

    for (let i = 0; i < dayOrder.length; i++) {
        const day = dayOrder[i];
        const prevDay = dayOrder[(i + 6) % 7];
        const nextDay = dayOrder[(i + 1) % 7];
        const current = cloned[day];
        const prev = cloned[prevDay];
        const next = cloned[nextDay];
        const entries: EntryModel[] = [];

        if (current) {
            const currentEntriesSorted = [...(current.entries ?? [])].sort((a, b) =>
                a.startTime.localeCompare(b.startTime)
            );

            for (const entry of currentEntriesSorted) {
                // Keep 00:00–00:00 as "24 hours" (never part of split/merge)
                if (isTwentyFourHourGuard(entry)) {
                    entries.push(entry);
                    continue;
                }

                const crossesMidnight = isCrossingMidnight(entry, next);

                // Only skip 00:00-start entries if previous day continues into this day (same type)
             
                if (entry.startTime === "00:00" && !crossesMidnight) {
                    const prevHasCrossingSameType =
                        !!prev?.entries?.some((e) => {
                            // For prev-day crossing check, "next" for prev is "current"
                            return isCrossingMidnight(e, current) && e.type === entry.type;
                        });

                    if (prevHasCrossingSameType) {
                        // This entry is likely the carried-over "second half"
                        continue;
                    }

                    // Otherwise it's a legit midnight-start shift (like 00:00–08:30)
                    entries.push(entry);
                    continue;
                }

                if (crossesMidnight) {
                    // Only match REAL continuation pieces (never match 00:00–00:00)
                    const matchIndex = next?.entries?.findIndex((e) =>
                        isRealContinuationPiece(e, entry.type)
                    );

                    if (matchIndex !== undefined && matchIndex !== -1) {
                        const matching = next!.entries[matchIndex];
                        entries.push({ ...entry, endTime: matching.endTime });
                        next!.entries.splice(matchIndex, 1);
                        continue;
                    }
                }

                entries.push(entry);
            }
        }

        result[day] = {
            enabled:
                current?.enabled === false
                    ? false
                    : (current?.enabled || entries.length > 0),
            afterHours24: current?.afterHours24 || false,
            entries,
        };
    }

    return result;
}

export function removeEmptyTimeEntries(
    hours: { [key: string]: HoursEntryModel }
): { [key: string]: HoursEntryModel } {

    const result: { [key: string]: HoursEntryModel } = {};

    for (const [day, model] of Object.entries(hours)) {
        result[day] = {
            ...model,
            entries: model.entries.filter(e => {
                const start = e.startTime?.trim() ?? "";
                const end = e.endTime?.trim() ?? "";
                return !(start === "" && end === "");
            })
        };
    }

    return result;
}

export function isCarryoverFromPreviousDay(e: EntryModel): boolean {
    if (!e?.startTime || !e?.endTime) return false;

    const start = e.startTime.trim();
    const end = e.endTime.trim();

    //24h-style entry
    if (start === "00:00" && end === "00:00") {
        return false;
    }

    // carryover rule: starts at midnight and ends before cutoff
    return start === "00:00" && end < CARRYOVER_CUTOFF;
}



export function objectsEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    const valA = a[key];
    const valB = b[key];

    if (typeof valA === "object" && typeof valB === "object") {
      if (!deepEqual(valA, valB)) return false;
    } else if (valA !== valB) {
      return false;
    }
  }

  return true;
}

function deepEqual(a: any, b: any, path: string = ""): boolean {
    if (a === b) return true;

    // Handle primitive or null mismatch
    if (!a || !b || typeof a !== "object" || typeof b !== "object") {
        return false;
    }

    // Handle arrays (order-insensitive)
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }

        // Compare ignoring order
        const sortedA = [...a].sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y)));
        const sortedB = [...b].sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y)));

        for (let i = 0; i < sortedA.length; i++) {
            if (!deepEqual(sortedA[i], sortedB[i], `${path}[${i}]`)) {
                return false;
            }
        }
        return true;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    // ignore extra keys in b (obj a has .edit key)
    for (const key of keysA) {
        const newPath = path ? `${path}.${key}` : key;
        if (!Object.prototype.hasOwnProperty.call(b, key)) {
            continue;
        }

        if (!deepEqual(a[key], b[key], newPath)) {
            return false;
        }
    }

    return true;
}

export function generalInfoEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;

    const keysA = Object.keys(a).filter(k => k !== "details");

    for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) {
            continue;
        }

        if (!deepEqual(a[key], b[key], key)) {
            return false;
        }
    }

    return true;
}


export function hasMarketingEdit(location: LocationModel, selectedSite: string): boolean {
    const details = location.details ?? {};
    const hasEmptyKey = Object.prototype.hasOwnProperty.call(details, "");

    const detailToCheck = hasEmptyKey
        ? details[""]
        : details[selectedSite];

    return !!detailToCheck?.edit;
}

export function marketingInfoEqual(location: LocationModel): boolean {
    const details = location.details ?? {};
    const keys = Object.keys(details);
    if (keys.length === 0) return true;

    for (const key of keys) {
        const detail = details[key];
        const edited = detail?.edit;

        if (!edited || typeof edited !== "object") continue;

        const { edit, ...original } = detail;

        if (!objectsEqual(original, edited)) return false;
    }

    return true;
}

/**
 * Wrapper that checks equality, applies edits if all equal,
 * and removes all `edit` properties after applying.
 */
export async function handleMarketingInfo(location: LocationModel, applyAllMarketingEdits: (id: string) => Promise<boolean>) {
    if (!location?.id) return;
    const isEqual = marketingInfoEqual(location);
    if (!isEqual) return;
    const result = await applyAllMarketingEdits(location.id);

    if (result) {
        const details = location.details ?? {};
        for (const key in details) {
            if (details[key]?.edit) {
                delete details[key].edit;
            }
        }
    }
}

export async function applyAllGeneralEdits(id: string | undefined) {
    const manager = await context.getService<ILocationEditManager>('ILocationEditManager');
    try {
        if (id) {
            let result = await manager.applyAllLocationEdits(id);
            if (result && result.ok) {
                return true;
            } else {
                return false;
            }
        }
    } catch (error) {
        console.error('Apply All Location Edits Failed:', error);
    }
    return false;
}

export async function applyAllMarketingEdits(id: string | undefined) {
    const manager = await context.getService<ILocationEditManager>('ILocationEditManager');
    try {
        if (id) {
            let result = await manager.applyAllMarketingEdits(id);
            if (result && result.ok) {
                return true;
            } else {
                return false;
            }
        }
    } catch (error) {
        console.error('Apply All Marketing Edits Failed:', error);
    }
    return false;
}

export const getDetailsKey = (location: LocationModel, selectedSite: string): string =>
    (Object.keys(location.details ?? {})[0] || '') !== '' ? selectedSite : '';

export const getLocationCodesByType = function (location: LocationModel | undefined, allServices: ServiceModel[], type: ServiceType): string[] {
    if (!location) return [];

    return allServices
        .filter(s =>
            s.type === type &&
            location.services.includes(s.code)
        ).map(s => s.code);
}

export const getServiceListModelsByType = function (location: LocationModel | undefined, allServices: ServiceModel[], type: ServiceType): IServiceListModel[] {
    return allServices.filter((s) => {
        return s.type == type
            && location?.services.some((l) => { return l == s.code; });
    }).map((s) => {
        return {
            code: s.code,
            name: s.name,
            addFee: false,
            selectedAmenities: [],
            availableAmenities: [],
            sites: s.sites,
            priority: s.priority,
            hasData: false,
            amenities: getListingAmenities(location, s, allServices),
        } as IServiceListModel;
    });
}

const getListingAmenities = function (location: LocationModel | undefined, service: ServiceModel, allServices: ServiceModel[]): IServiceListModel[] {
    if (service.children.length == 0) {
        return [];
    }

    return allServices.filter((s) => {
        return s.type == ServiceType.Amenity
            && service.children.some((l) => { return l == s.code; })
            && location?.properties.additionalData.some((l) => { return l.key == s.code; });
    }).map((s) => {
        return getAmenity(s, location);
    });
}

const getAmenity = function (service: ServiceModel, location?: LocationModel): IServiceListModel {
    if (location) {
        let addData = location.properties.additionalData
            .filter((d) => { return d.isAmentity && d.key == service.code; });

        if (addData.length > 0 && addData[0].value && addData[0].value !== null) {
            return {
                code: service.code,
                name: service.name,
                detail: addData[0].value,
                addFee: addData[0].additionalFee,
                availableAmenities: [],
                selectedAmenities: [],
                sites: service.sites,
                priority: service.priority,
                hasData: service.hasData,
            };
        }
    }

    return {
        code: service.code,
        name: service.name,
        addFee: false,
        availableAmenities: [],
        selectedAmenities: [],
        sites: service.sites,
        priority: service.priority,
        hasData: service.hasData,
        detail: "",
    };
}

export const onlyDigits = function (input: string | undefined): string | undefined {
    if (input == undefined) return undefined;
    return input.replace(/\D/g, "");
}

export const formatPhone = function (num: string | undefined) {
    if (num == undefined) return undefined;
    const digits = num.replace(/\D/g, "");

    if (digits.length === 9) {
        const area = digits.slice(0, 3);
        const part1 = digits.slice(3, 6);
        const part2 = digits.slice(6);
        return `(${area}) ${part1}-${part2}`;
    }

    if (digits.length >= 10) {

        const extra = digits.length > 10 ? digits.slice(0, digits.length - 10) : "";
        const last10 = digits.slice(-10);
        const area = last10.slice(0, 3);
        const part1 = last10.slice(3, 6);
        const part2 = last10.slice(6);

        return `${extra ? extra + " " : ""}(${area}) ${part1}-${part2}`;
    }

    return num;
}
